import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useNavigate, useParams } from "react-router-dom";
import OperationApi from "../../services/OperationApi";

// Design System
import Input from "../../components/Input/Input";
import FormCard from "../../components/FormCard/FormCard";

const OperationDetails: React.FC = () => {
  const { t: translate } = useTranslation("form");
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  interface Unidade {
    id: string;
    name: string;
  }
  const [unidade, setUnidade] = useState<Unidade | null>(null);

  interface Area {
    id: string;
    name: string;
  }
  const [area, setArea] = useState<Area | null>(null);

  interface Linha {
    id: string;
    name: string;
  }
  const [linha, setLinha] = useState<Linha | null>(null);

  interface Sublinha {
    id: string;
    name: string;
  }
  const [sublinha, setSublinha] = useState<Sublinha | null>(null);

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [rank, setRank] = useState("");
  const [tipos, setTipos] = useState([]);
  const [tipo, setTipo] = useState(null);
  const [status, setStatus] = useState<"1" | "0" | null>(null);

  const statusOptions = [
    { label: "Ativa", value: "1", id: "1" },
    { label: "Inativa", value: "0", id: "0" },
  ];

  useEffect(() => {
    const fetchInitialData = async (): Promise<void> => {
      if (!id) return;
      try {
        const opRes = await OperationApi.GetOperationDetailsById(id);
        const sub = opRes.data.subLine;
        const lin = sub?.line;
        const ar = lin?.area;
        const uni = ar?.productionUnit;
        
        if (uni) setUnidade(uni);
        if (ar) setArea(ar);
        if (lin) setLinha(lin);
        if (sub) setSublinha(sub);
        
        setNome(opRes.data.name);
        setDescricao(opRes.data.description);
        setRank(opRes.data.rank?.toString() ?? "");
        
        const typesRes = await OperationApi.GetAllOperationTypes();
        setTipos(typesRes.data);
        setTipo(opRes.data.operationTypeId);
        setStatus(opRes.data.isActive ? "1" : "0");
      } catch {
        displayToast("error", "Erro ao carregar dados da operação", "", false, 3000);
      }
    };
    fetchInitialData();
  }, [id, displayToast]);

  return (
    <FormCard
      title={translate("OperationDetails")}
      icon="info"
      onSubmit={() => {}}
      onCancel={() => navigate("/operations")}
      cancelButtonLabel={translate("GoBack") || "Voltar"}
      numColumns={12}
      disabled
    >
      {/* Unidade de Produção */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("productionUnit")}
          value={unidade?.name || "—"}
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Área */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("area")}
          value={area?.name || "—"}
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Linha */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("line")}
          value={linha?.name || "—"}
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Sublinha */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("subline")}
          value={sublinha?.name || "—"}
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Nome da Operação */}
      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="text"
          label={translate("operationName")}
          value={nome || "—"}
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Descrição */}
      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="textArea"
          label={translate("operationDescription")}
          value={descricao || "—"}
          rows={3}
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Rank */}
      <div style={{ gridColumn: "span 4" }}>
        <Input
          type="text"
          label={translate("operationRank")}
          value={rank || "—"}
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Tipo */}
      <div style={{ gridColumn: "span 4" }}>
        <Input
          type="select"
          label={translate("Type")}
          value={tipo || ""}
          options={tipos}
          optionLabel="name"
          optionValue="id"
          disabled
          onChange={() => {}}
        />
      </div>
      
      {/* Status */}
      <div style={{ gridColumn: "span 4" }}>
        <Input
          type="select"
          label={translate("status")}
          value={status || ""}
          options={statusOptions}
          disabled
          onChange={() => {}}
        />
      </div>
    </FormCard>
  );
};

export default OperationDetails;
